import { storeToRefs } from 'pinia'
import { useGamesStore } from '#src/stores/gamesStore'
import type { CmsBootstrapRequest } from '#src/interfaces/CmsBootstrapRequest'
import type { CmsAppName, CmsBootstrapByApp, CmsTemplate } from '#src/interfaces/CmsConfig'
import { fetchAppBootstrap } from './cmsApiService'
import { getCmsContentBlockHtml } from '#src/utils/cmsUtils'

const initCmsService = async <A extends CmsAppName>(
  appName: A,
  request: CmsBootstrapRequest
): Promise<CmsBootstrapByApp[A]> => {
  const cmsConfig = await fetchAppBootstrap(appName, request)
  return cmsConfig.data
}

const parseCmsContentBlock = (contentBlocks: CmsTemplate['contentBlocks']) => {
  return contentBlocks.length ? contentBlocks.map(getCmsContentBlockHtml).join('') : null
}

const createSpacesFromTemplates = (templates: CmsTemplate[] = []) => {
  const { gameLinks } = storeToRefs(useGamesStore())
  const spaces: Record<string, { templates: { [key: string]: string } }> = {}
  // Utility to split context keys safely
  const getContextParts = (key: string) => key.split('_')
  const getTemplateType = (t: CmsTemplate) => getContextParts(t.context.key)[0]

  const gamesSpecificTemplates = templates.filter((t) => (t.games?.length ?? 0) > 0)

  const globalBetslipTemplates = templates.filter((t) => {
    const isBetslip = getTemplateType(t) === 'betslip'
    const isGameOverride = (t.games?.length ?? 0) > 0
    return isBetslip && !isGameOverride
  })

  const otherTemplates = templates.filter((t) => getTemplateType(t) !== 'betslip')

  // Register non-game templates (e.g. test_test, voucher_payment) outside the games loop so they
  // are available even when no games are loaded — required for back-office apps that bootstrap
  // CMS templates without booting games.
  for (const t of otherTemplates) {
    const [spaceName, templateName] = getContextParts(t.context.key)
    if (!spaceName || !templateName) continue

    if (!spaces[spaceName]?.templates) {
      spaces[spaceName] = {
        templates: {
          [templateName]: parseCmsContentBlock(t.contentBlocks) ?? ''
        }
      }
    }

    spaces[spaceName].templates[templateName] = parseCmsContentBlock(t.contentBlocks) ?? ''
  }

  // register game templates
  for (const gameLink of gameLinks.value) {
    const gameSpecific = gamesSpecificTemplates.filter((t) =>
      t.games?.some((g) => g.externalId === gameLink.installation.game.externalId)
    )

    // Fallback to global templates if not overridden
    const globalTemplates = globalBetslipTemplates.filter(
      (gbt) => !gameSpecific.some((gst) => gst.context.key === gbt.context.key)
    )

    const finalTemplates = [...globalTemplates, ...gameSpecific]
    const spaceName = `games${gameLink.code}`

    spaces[spaceName] = {
      templates: Object.fromEntries(
        finalTemplates.map((t) => [t.context.key, parseCmsContentBlock(t.contentBlocks) ?? ''])
      )
    }
  }

  return spaces
}

export { initCmsService, parseCmsContentBlock, createSpacesFromTemplates }
